import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ReviewCard from './ReviewCard';
import { reviewsData } from '../data/reviewsData';

const { FiSearch, FiFilter, FiStar, FiX, FiArrowLeft } = FiIcons;

const ReviewsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('rating'); // rating, title, newest

  // Filter and sort reviews
  const filteredAndSortedReviews = reviewsData
    .filter(review => 
      review.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      review.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return b.rating - a.rating;
        case 'title':
          return a.title.localeCompare(b.title);
        case 'newest':
          return new Date(b.date || '2025-01-01') - new Date(a.date || '2025-01-01');
        default:
          return 0;
      }
    });

  const averageRating = reviewsData.reduce((sum, review) => sum + review.rating, 0) / reviewsData.length;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  return (
    <div className="app">
      <div className="stars"></div>
      
      <main className="main">
        <div className="container">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            style={{ marginBottom: '20px' }}
          >
            <Link
              to="/"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '50px',
                padding: '12px 20px',
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: '500',
                textDecoration: 'none',
                backdropFilter: 'blur(20px)',
                transition: 'all 0.3s ease',
                width: 'fit-content'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(0, 255, 136, 0.1)';
                e.target.style.borderColor = '#00ff88';
                e.target.style.boxShadow = '0 0 20px rgba(0, 255, 136, 0.2)';
                e.target.style.transform = 'translateX(-5px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.target.style.boxShadow = 'none';
                e.target.style.transform = 'translateX(0)';
              }}
            >
              <SafeIcon 
                icon={FiArrowLeft} 
                style={{ 
                  fontSize: '18px',
                  transition: 'transform 0.3s ease'
                }} 
              />
              <span style={{
      backgroundColor: 'rgba(255, 255, 255, 0.1)'
    }}>Back Startpage</span>
            </Link>
          </motion.div>

          {/* Header Section */}
          <motion.div 
            className="header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="logo">
              <div className="alien-icon">📝</div>
              <h1 style={{
                fontSize: '38px',
                fontWeight: '800'
              }}>Tool Reviews</h1>
            </div>
            <p className="tagline">
              In-depth reviews of the best tools for creators, entrepreneurs, and professionals. 
              Real testing, honest opinions, no fluff.
            </p>
            
            {/* Stats */}
            <motion.div 
              className="category-tabs"
              style={{ justifyContent: 'center', marginBottom: '20px' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              <div className="tab" style={{ cursor: 'default' }}>
                <SafeIcon icon={FiStar} style={{ color: '#00ff88', marginRight: '8px' }} />
                {averageRating.toFixed(1)} Average Rating
              </div>
              <div className="tab" style={{ cursor: 'default' }}>
                📊 {reviewsData.length} Reviews
              </div>
            </motion.div>

            {/* Search and Filter Section */}
            <motion.div 
              style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '16px',
                maxWidth: '600px',
                margin: '0 auto',
                width: '100%'
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              {/* Search Bar */}
              <div className="search-container">
                <motion.div 
                  className="search-wrapper"
                  whileHover={{ scale: 1.02 }}
                  whileFocus={{ scale: 1.02 }}
                >
                  <input
                    type="text"
                    placeholder="Search reviews across the universe..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  <div className="search-icon">🔍</div>
                </motion.div>
              </div>

              {/* Sort Dropdown */}
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <motion.div
                  style={{ position: 'relative' }}
                  whileHover={{ scale: 1.02 }}
                >
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={{
                      padding: '12px 40px 12px 20px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '50px',
                      color: '#ffffff',
                      fontSize: '16px',
                      backdropFilter: 'blur(20px)',
                      cursor: 'pointer',
                      outline: 'none',
                      appearance: 'none',
                      minWidth: '200px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#00ff88';
                      e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                      e.target.style.boxShadow = '0 0 20px rgba(0, 255, 136, 0.3)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                      e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="rating" style={{ background: '#000', color: '#fff' }}>🌟 Highest Rated</option>
                    <option value="title" style={{ background: '#000', color: '#fff' }}>🔤 Alphabetical</option>
                    <option value="newest" style={{ background: '#000', color: '#fff' }}>🆕 Newest First</option>
                  </select>
                  <div style={{
                    position: 'absolute',
                    right: '20px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '16px',
                    pointerEvents: 'none'
                  }}>⚡</div>
                </motion.div>
              </div>
            </motion.div>

            {/* Results Count */}
            <motion.div 
              style={{ textAlign: 'center', marginTop: '20px' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.6 }}
            >
              <p style={{ color: '#a0a0a0', fontSize: '16px' }}>
                Showing {filteredAndSortedReviews.length} of {reviewsData.length} reviews
                {searchTerm && (
                  <span style={{ marginLeft: '8px' }}>
                    for "<span style={{ color: '#00ff88', fontWeight: '600' }}>{searchTerm}</span>"
                  </span>
                )}
              </p>
            </motion.div>
          </motion.div>

          {/* Reviews Grid */}
          <AnimatePresence mode="wait">
            <motion.div 
              className="tools-grid"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              key={filteredAndSortedReviews.length}
            >
              {filteredAndSortedReviews.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="no-results"
                  style={{
                    gridColumn: '1 / -1',
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: '#888',
                    fontSize: '1.2rem'
                  }}
                >
                  <div style={{ fontSize: '4rem', marginBottom: '20px' }}>📝</div>
                  <div>No reviews found in this galaxy...</div>
                  <div style={{ fontSize: '0.9rem', marginTop: '10px', opacity: 0.7 }}>
                    Try adjusting your search or clear filters
                  </div>
                  {searchTerm && (
                    <motion.button
                      onClick={() => setSearchTerm('')}
                      className="btn btn-primary"
                      style={{ marginTop: '20px' }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Clear Search
                    </motion.button>
                  )}
                </motion.div>
              ) : (
                filteredAndSortedReviews.map((review, index) => (
                  <motion.div
                    key={review.slug}
                    variants={itemVariants}
                    layout
                    transition={{ duration: 0.3 }}
                  >
                    <ReviewCard review={review} />
                  </motion.div>
                ))
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default ReviewsPage;